import { useState } from 'react';
import { EDITABLE_STATUSES } from '../lib/types';
import type { Reservation, ReservationEdit } from '../lib/types';
import { addDays } from '../lib/format';

/**
 * Edit a booking in place, instead of cancelling it and raising a new
 * one just to fix a project number or push a date.
 *
 * The resource isn't here — Reassign owns that, because it has to check
 * the target is bookable. Cancelling isn't either: it records who
 * cancelled and why, which a plain field edit would skip.
 *
 * Only changed fields are sent, so two people editing different fields
 * of the same booking don't clobber each other.
 */
export function EditReservationModal({
  target,
  saving,
  onConfirm,
  onClose,
}: {
  target: Reservation;
  saving: boolean;
  onConfirm: (target: Reservation, changes: Partial<ReservationEdit>) => void;
  onClose: () => void;
}) {
  const initial: ReservationEdit = {
    projectNumber: target.projectNumber,
    projectName: target.projectName,
    startDate: target.startDate,
    endDate: target.endDate,
    reservedBy: target.reservedBy,
    status: (EDITABLE_STATUSES as readonly string[]).includes(target.status)
      ? (target.status as ReservationEdit['status'])
      : 'Confirmed',
    notes: target.notes,
  };
  const [form, setForm] = useState<ReservationEdit>(initial);

  const set = <K extends keyof ReservationEdit>(
    key: K,
    value: ReservationEdit[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const originalSpan = Math.round(
    (new Date(target.endDate).getTime() - new Date(target.startDate).getTime()) /
      86_400_000,
  );

  function moveStart(next: string) {
    // Drag the end along by the original duration — "it slipped a week"
    // nearly always means the whole booking moved — but never silently
    // rewrite an end date into an incoherent range.
    setForm((prev) => ({
      ...prev,
      startDate: next,
      endDate:
        next && prev.endDate && originalSpan >= 0
          ? addDays(next, originalSpan)
          : prev.endDate,
    }));
  }

  const changes: Partial<ReservationEdit> = {};
  for (const key of Object.keys(initial) as (keyof ReservationEdit)[]) {
    if (form[key] !== initial[key]) {
      changes[key] = form[key] as never;
    }
  }

  const invalidRange =
    form.startDate !== '' && form.endDate !== '' && form.endDate < form.startDate;
  const ready =
    Object.keys(changes).length > 0 &&
    !invalidRange &&
    form.projectNumber.trim() !== '' &&
    form.reservedBy.trim() !== '' &&
    form.startDate !== '' &&
    form.endDate !== '';

  return (
    <div className="modal-overlay is-nested" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: 440 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <span
            style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}
          >
            Edit reservation
          </span>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div
          style={{
            background: 'var(--bg-tertiary)',
            border: '0.5px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '8px 12px',
            marginBottom: 14,
            fontSize: 11,
            color: 'var(--text-secondary)',
          }}
        >
          {target.reference} · {target.equipment || 'Unassigned'}
          <div style={{ marginTop: 2 }}>
            Use Reassign to change the resource, Cancel to cancel it.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label className="form-label">Project number</label>
              <input
                style={{ width: '100%' }}
                value={form.projectNumber}
                onChange={(e) => set('projectNumber', e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="form-label">Project name</label>
              <input
                style={{ width: '100%' }}
                value={form.projectName}
                onChange={(e) => set('projectName', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label className="form-label">Start date</label>
              <input
                type="date"
                style={{ width: '100%' }}
                value={form.startDate}
                onChange={(e) => moveStart(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="form-label">End date</label>
              <input
                type="date"
                style={{ width: '100%' }}
                value={form.endDate}
                min={form.startDate || undefined}
                onChange={(e) => set('endDate', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label className="form-label">Reserved by</label>
              <input
                style={{ width: '100%' }}
                value={form.reservedBy}
                onChange={(e) => set('reservedBy', e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="form-label">Status</label>
              <select
                className="form-input"
                style={{ width: '100%' }}
                value={form.status}
                onChange={(e) =>
                  set('status', e.target.value as ReservationEdit['status'])
                }
              >
                {EDITABLE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="form-label">Notes</label>
            <input
              style={{ width: '100%' }}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </div>

          {invalidRange && (
            <div style={{ fontSize: 11, color: 'var(--red-fg)' }}>
              The end date is before the start date.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              className="btn-save"
              disabled={saving || !ready}
              onClick={() => onConfirm(target, changes)}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <button className="btn-cancel" onClick={onClose}>
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
